import { useState } from 'react';
import { useMutation } from 'react-apollo';
import omit from 'lodash.omit';
import pMap from 'p-map-browser';

import usePhotosUpload from '../../../../hooks/usePhotosUpload';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  rosterIdentityUpdate as rosterIdentityUpdateMutation,
  rosterIdentityCreateFromIdentity as rosterIdentityCreateFromIdentityMutation,
  rosterIdentityLinkedIdentityCreate as rosterIdentityLinkedIdentityCreateMutation,
  rosterIdentityLinkedIdentityUpdate as rosterIdentityLinkedIdentityUpdateMutation,
  rosterIdentityLinkedIdentityAddressCreate as rosterIdentityLinkedIdentityAddressCreateMutation,
  rosterIdentityLinkedIdentityAddressUpdate as rosterIdentityLinkedIdentityAddressUpdateMutation,
  selectedIdentityUpdate as selectedIdentityUpdateMutation,
  rosterIdentitiesCreate as rosterIdentitiesCreateMutation,
} from '../../../../graphql/mutations/roster-identity';
import {
  identityAlbumFolderTagsCreate as identityAlbumFolderTagsCreateMutation,
  identityAlbumFolderTagsDelete as identityAlbumFolderTagsDeleteMutation,
} from '../../../../graphql/mutations/album-folder-tags';
import { getIdentityAlbumFolderTagsQuery } from '../../../temp/views/onboarding/TagSelection/graphql/queries/getIdentityAlbumFolderTags';
import { AlbumFolderTags } from '../../../../graphql/queries/album-folder-tags';
import { hierarchyOwnerPurchasedIdentityCreate as hierarchyOwnerPurchasedIdentityCreateMutation } from '../../../../graphql/mutations/hierarchy-items';

const useRosterIdentityUpdate = ({
  onPhotoUploadSuccess,
  currentClaimedIdentity,
} = {}) => {
  const [rosterIdentityUpdate] = useMutation(rosterIdentityUpdateMutation);
  const [selectedIdentityUpdate] = useMutation(selectedIdentityUpdateMutation);
  const [rosterIdentitiesCreate] = useMutation(rosterIdentitiesCreateMutation);
  const [rosterIdentityCreateFromIdentity] = useMutation(
    rosterIdentityCreateFromIdentityMutation,
  );
  const [rosterIdentityLinkedIdentityCreate] = useMutation(
    rosterIdentityLinkedIdentityCreateMutation,
  );
  const [rosterIdentityLinkedIdentityUpdate] = useMutation(
    rosterIdentityLinkedIdentityUpdateMutation,
  );
  const [rosterIdentityLinkedIdentityAddressCreate] = useMutation(
    rosterIdentityLinkedIdentityAddressCreateMutation,
  );
  const [rosterIdentityLinkedIdentityAddressUpdate] = useMutation(
    rosterIdentityLinkedIdentityAddressUpdateMutation,
  );

  const [tagsCreate] = useMutation(identityAlbumFolderTagsCreateMutation);
  const [tagsDelete] = useMutation(identityAlbumFolderTagsDeleteMutation);

  const [hierarchyOwnerPurchasedIdentityCreate] = useMutation(
    hierarchyOwnerPurchasedIdentityCreateMutation,
  );

  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const { onPhotoUpload } = usePhotosUpload({
    onPhotoUploadSuccess,
  });
  /**
   * @typedef {Object} LinkedIdentity
   * @property {String} phone_number Linked Identity Phone number.
   * @property {Array<Object>} addresses A list of RosterIdentityAddresses.
   */

  /**
   * Updates a Roster Identity.
   *
   * @param {Object} input The options object.
   * @param {String} input.first_name Roster ID First Name.
   * @param {String} input.last_name Roster ID Last Name.
   * @param {File} input.selfie An image file to replace the current selfie.
   * @param {String} input.id The roster Identity Id.
   * @param {String} input.account_id The Account ID.
   * @param {String} [input.identity_id] The Identity ID. If passed down, the mutation createRosterIdentityFromIdentity will be used.
   * @param {Array<LinkedIdentity>} [input.linked_identities] Linked identities (apply only on create).
   * @param {Boolean} input.photo_share_blocked Roster ID NPTS value.
   */
  const onRosterIdentityUpdate = async input => {
    setUpdating(true);
    setUpdateSuccess(false);
    setUpdateError('');

    try {
      let selfieId;

      const {
        selfie,
        account_id: accountId,
        identity_id: identityId,
        linked_album_ids: linkedAlbumIds = [],
        linked_album_folder_ids: linkedAlbumFolderIds = [],
        owner_purchased_album_ids: ownerPurchasedAlbumIds = [],
        claimed_identity: claimed,
        selected_identity_id: selectedIdentityId,
        email_address: emailAddress,
        email_address_id: emailAddressId,
        create_album_folder_tags: createTags = [],
        roster_identity_link_id: rosterIdentityLinkId,
        ...rosterIdentityMutationInput
      } = input;

      const {
        grade: linkGrade,
        teacher_name: linkTeacherName,
        class_name: linkClassName,
        organization_ref_id: linkRefId,
      } = rosterIdentityMutationInput;

      // The school fields live on the roster identity link for the event, and
      // that is what the member views read. They are only kept on the roster
      // identity itself for a member that has no link at all - an address book
      // contact, say.
      const linkFields = {
        grade: linkGrade,
        teacher_name: linkTeacherName,
        class_name: linkClassName,
        ref_id: linkRefId,
      };

      // Every gallery or album the member joins goes to the platform once, as
      // a link. The platform merges `links` with the older
      // `linked_album_ids` / `linked_album_folder_ids` inputs into one list,
      // so sending an item through both registered its link twice and
      // hive-next then joined each linked account to it twice.
      const links = [
        ...new Set([...linkedAlbumFolderIds, ...linkedAlbumIds]),
      ].map(hierarchyItemId => ({
        hierarchy_item_id: hierarchyItemId,
        ...linkFields,
      }));

      if (selfie) {
        const [photoUploadData] = await onPhotoUpload({
          files: [selfie],
          accountId,
          selfie_verify: true,
        });

        if (photoUploadData.error) {
          throw photoUploadData.error;
        }

        selfieId = photoUploadData.photoId;
      }

      // Update:
      if (input.roster_identity_id) {
        // Check if there are linked identities to update.
        // Do note that this is performed first so that the update
        // mutation for the roster ID returns a complete-updated object when
        // run (Plus rosterIdentityUpdate doesnt support linked identities at the time of writing)
        const {
          linked_identities: linkedIdentities,
          ...rosterIdentityUpdateInput
        } = rosterIdentityMutationInput;

        const [linkedIdentity] = linkedIdentities ?? [];

        const hasAddress = (linkedIdentity?.addresses?.length ?? 0) > 0;
        // A phone number on its own is enough to need a linked identity. The
        // caller leaves the id off when the number cannot go on the existing
        // one (no linked identity yet, or only a marketing account, which
        // never holds a phone), and a new one is created for it below.
        const hasPhoneNumber = !!linkedIdentity?.phone_number;

        if (hasAddress || hasPhoneNumber) {
          // At the time, we only support 1 linked  identity to be created/updated.

          if (linkedIdentity.id) {
            // Update/Create the address of the linked Identity.
            const [address] = linkedIdentity.addresses ?? [];

            if (address?.id) {
              // Update this address
              await rosterIdentityLinkedIdentityAddressUpdate({
                variables: {
                  input: {
                    country_id: address.country?.id,
                    roster_identity_linked_identity_address_id: address.id,
                    ...omit(address, ['id', 'country', '__typename']),
                  },
                },
              });
            } else if (address) {
              // Create new address:
              await rosterIdentityLinkedIdentityAddressCreate({
                variables: {
                  input: {
                    roster_identity_linked_identity_id: linkedIdentity.id,
                    ...address,
                  },
                },
              });
            }

            if (hasPhoneNumber) {
              // The number belongs to the Account behind this linked identity,
              // so it needs its own mutation. The caller only sends a phone
              // when it changed and the PRO is allowed to change it.
              await rosterIdentityLinkedIdentityUpdate({
                variables: {
                  input: {
                    roster_identity_linked_identity_id: linkedIdentity.id,
                    phone_number: linkedIdentity.phone_number,
                  },
                },
              });
            }
          } else {
            // Create the linked identity as a whole new thing.
            await rosterIdentityLinkedIdentityCreate({
              variables: {
                input: {
                  roster_identity_id: input.roster_identity_id,
                  linked_identity: omit(linkedIdentity, ['id']),
                },
              },
            });
          }
        }

        const {
          data: {
            rosterIdentityUpdate: { roster_identity: rosterIdentity },
          },
        } = await rosterIdentityUpdate({
          variables: {
            input: {
              ...(rosterIdentityLinkId
                ? {
                    ...omit(rosterIdentityUpdateInput, [
                      'grade',
                      'teacher_name',
                      'class_name',
                      'organization_ref_id',
                    ]),
                    update_links: [
                      {
                        roster_identity_link_id: rosterIdentityLinkId,
                        ...linkFields,
                      },
                    ],
                  }
                : rosterIdentityUpdateInput),
              selfie_photo_id: selfieId,
              // Editing the existing address has to update its record.
              // Creating one here instead would leave the old address in
              // place and simply add a second one.
              ...(emailAddressId
                ? {
                    update_email_addresses: emailAddress
                      ? [{ id: emailAddressId, email_address: emailAddress }]
                      : [],
                    delete_email_address_ids: emailAddress
                      ? []
                      : [emailAddressId],
                  }
                : {
                    create_email_addresses: [emailAddress].filter(Boolean),
                  }),
            },
          },
        });

        if (claimed !== currentClaimedIdentity && selectedIdentityId) {
          await selectedIdentityUpdate({
            variables: {
              input: {
                id: selectedIdentityId,
                claimed,
              },
            },
          });
        }

        if (ownerPurchasedAlbumIds?.length > 0) {
          const identitiesForPurchase = [
            identityId,
            ...(rosterIdentity?.linked_identities?.records?.map(
              ({ identity }) => identity?.id,
            ) ?? []),
          ].filter(Boolean);

          if (identitiesForPurchase.length > 0) {
            await pMap(
              identitiesForPurchase,
              async linkedIdentityIdentityId => {
                await pMap(ownerPurchasedAlbumIds, async albumId =>
                  hierarchyOwnerPurchasedIdentityCreate({
                    variables: {
                      input: {
                        identity_id: linkedIdentityIdentityId,
                        hierarchy_item_id: albumId,
                      },
                    },
                  }),
                );
              },
            );
          }
        }

        setUpdateSuccess(true);
        return rosterIdentity;
      }

      // Create using identity as reference.
      if (identityId) {
        const {
          linked_identities: linkedIdentities = [],
          ...rosterIdentityCreateFromIdentityInput
        } = rosterIdentityMutationInput;

        const [mainLinkedAddentity] = linkedIdentities;

        const {
          data: {
            rosterIdentityCreateFromIdentity: {
              roster_identity: rosterIdentity,
            },
          },
        } = await rosterIdentityCreateFromIdentity({
          variables: {
            input: {
              ...rosterIdentityCreateFromIdentityInput,
              identity_id: identityId,
              links,
              addresses: mainLinkedAddentity?.addresses ?? [],
            },
          },
        });

        if (selfieId) {
          await rosterIdentityUpdate({
            variables: {
              input: {
                roster_identity_id: rosterIdentity.id,
                selfie_photo_id: selfieId,
              },
            },
          });
        }

        if (ownerPurchasedAlbumIds?.length > 0) {
          const identitiesForPurchase = [
            identityId,
            ...(rosterIdentity?.linked_identities?.records?.map(
              ({ identity }) => identity?.id,
            ) ?? []),
          ].filter(Boolean);

          if (identitiesForPurchase.length > 0) {
            await pMap(
              identitiesForPurchase,
              async linkedIdentityIdentityId => {
                await pMap(ownerPurchasedAlbumIds, async albumId =>
                  hierarchyOwnerPurchasedIdentityCreate({
                    variables: {
                      input: {
                        identity_id: linkedIdentityIdentityId,
                        hierarchy_item_id: albumId,
                      },
                    },
                  }),
                );
              },
            );
          }
        }

        setUpdateSuccess(true);
        return rosterIdentity;
      }

      // RosterIdentitiesCreateRosterIdentityInput has no photo_share_blocked,
      // so it is set with an update right after the member is created.
      const {
        photo_share_blocked: photoShareBlocked,
        ...rosterIdentityCreateInput
      } = rosterIdentityMutationInput;

      const validLinkedIdentities = (
        rosterIdentityCreateInput?.linked_identities ?? []
      ).filter(
        linkedIdentity =>
          !!linkedIdentity.phone_number ||
          !!linkedIdentity?.account?.email_verified,
      );

      // Create using account as reference
      const {
        data: {
          rosterIdentitiesCreate: {
            roster_identities: createdRosterIdentities = [],
            success: rosterIdentitiesCreateSuccess,
            ...invalidData
          },
        },
      } = await rosterIdentitiesCreate({
        variables: {
          input: {
            skip_errors: false,
            roster_identities: [
              {
                ...rosterIdentityCreateInput,
                linked_identities: validLinkedIdentities,
                email_addresses: emailAddress
                  ? [{ email_address: emailAddress }]
                  : [],
                links,
                tags: (createTags ?? []).map(tag => ({
                  album_folder_tag_id: tag.album_folder_tag.id,
                  jersey_number: tag.jersey_number,
                  uniform_album_folder_tag_level_id:
                    tag.uniform_album_folder_tag_level?.id,
                  uniform_album_folder_tag_level_team_id:
                    tag.uniform_album_folder_tag_level_team?.id,
                })),
              },
            ],
            // Email-only should always be claimed. Otherwise respect settings
            claimed:
              (!!emailAddress && !validLinkedIdentities?.length) || !!claimed,
            hierarchy_owner_purchase: {
              album_ids: ownerPurchasedAlbumIds,
            },
          },
        },
      });

      if (!rosterIdentitiesCreateSuccess) {
        const invalidValue =
          invalidData?.invalid_phone_numbers?.[0] ??
          invalidData?.invalid_ref_ids?.[0] ??
          invalidData?.invalid_names?.[0] ??
          invalidData?.invalid_addresses?.[0];

        throw new Error(
          `Failed to create member - invalid input: ${invalidValue}`,
        );
      }

      if (selfieId || photoShareBlocked) {
        await rosterIdentityUpdate({
          variables: {
            input: {
              roster_identity_id: createdRosterIdentities[0].id,
              ...(selfieId && { selfie_photo_id: selfieId }),
              ...(photoShareBlocked && { photo_share_blocked: true }),
            },
          },
        });
      }

      setUpdateSuccess(true);
      return createdRosterIdentities[0];
    } catch (error) {
      setUpdateError(getErrorString(error));
      return null;
    } finally {
      setUpdating(false);
    }
  };

  const onTagsCreate = async (input, { albumFolderId, identityId }) => {
    try {
      await tagsCreate({
        variables: {
          input: {
            identity_id: identityId,
            album_folder_tags: input.album_folder_tags,
          },
        },
        refetchQueries: [
          {
            query: getIdentityAlbumFolderTagsQuery,
            variables: {
              identityId,
              albumFolderId,
            },
          },
          {
            query: AlbumFolderTags,
            variables: {
              id: albumFolderId,
            },
          },
        ],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      throw new Error(getErrorString(error));
    }
  };

  const onTagsDelete = async (tagIds, { albumFolderId, identityId }) => {
    try {
      await tagsDelete({
        variables: {
          input: {
            ids: tagIds,
          },
        },
        refetchQueries: [
          {
            query: getIdentityAlbumFolderTagsQuery,
            variables: {
              identityId,
              albumFolderId,
            },
          },
          {
            query: AlbumFolderTags,
            variables: {
              id: albumFolderId,
            },
          },
        ],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      throw new Error(getErrorString(error));
    }
  };

  return {
    updating,
    updateError,
    updateSuccess,
    onRosterIdentityUpdate,
    onTagsCreate,
    onTagsDelete,
  };
};

export default useRosterIdentityUpdate;
